"use client";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const VIDEO_SRC = "/hero-demo.mp4";
const POSTER_SRC = "/hero-demo-poster.png";

export const HeroVideo = () => {
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);

  useEffect(() => {
    fetch(VIDEO_SRC, { method: "HEAD" })
      .then((r) => setHasVideo(r.ok))
      .catch(() => setHasVideo(false));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
      className="relative w-full max-w-6xl mx-auto px-4 mt-2 md:mt-4"
    >
      {/* Outer red glow */}
      <div
        aria-hidden
        className="absolute -inset-x-8 -inset-y-8 pointer-events-none blur-3xl opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(239,68,68,0.25), transparent 70%)",
        }}
      />

      {/* Browser frame */}
      <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 shadow-[0_0_80px_rgba(239,68,68,0.18)] overflow-hidden">
        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-red-500/10 pointer-events-none z-10" />

        {/* Window chrome */}
        <div className="flex items-center gap-2 border-b border-neutral-800 bg-neutral-900/80 px-4 py-2.5 relative z-20">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-red-500/90" />
            <span className="h-3 w-3 rounded-full bg-yellow-500/90" />
            <span className="h-3 w-3 rounded-full bg-green-500/90" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="rounded-md bg-neutral-800/80 px-3 py-1 min-w-[200px] max-w-md text-center">
              <span className="text-[11px] font-mono text-neutral-400">
                app.coldfire-crm.com
              </span>
            </div>
          </div>
          <div className="w-12" />
        </div>

        {/* Content */}
        <div className="relative aspect-video bg-gradient-to-br from-neutral-950 via-neutral-900 to-red-950/30">
          {hasVideo === true && (
            <video
              className="h-full w-full object-cover"
              src={VIDEO_SRC}
              poster={POSTER_SRC}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          )}
          {hasVideo === false && <DemoPlaceholder />}
        </div>
      </div>
    </motion.div>
  );
};

const DemoPlaceholder = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    {/* Subtle grid backdrop */}
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    />
    <div className="relative text-center">
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: [0.95, 1.02, 0.95] }}
        transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
        className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-500/15 ring-1 ring-red-500/40 backdrop-blur"
        style={{ boxShadow: "0 0 40px rgba(239,68,68,0.25)" }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-8 w-8 text-red-400 ml-1"
          aria-hidden
        >
          <path d="M8 5v14l11-7z" />
        </svg>
      </motion.div>
      <p className="text-base font-semibold text-neutral-200">
        Video folgt
      </p>
      <p className="mt-1.5 text-xs text-neutral-500">
        Blick ins System
      </p>
    </div>
  </div>
);
